import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_URL } from "../../../config";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  LineElement,
  PointElement,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import "./DepartmentDashboard.css";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  LineElement,
  PointElement
);

function Department() {
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [services, setServices] = useState([]);
  const navigate = useNavigate();

  const fetchDepartments = () => {
    axios
      .get(`${API_URL}/api/getDepartment`)
      .then((res) => setDepartments(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    fetchDepartments();

    axios
      .get(`${API_URL}/api/getemployeeData`)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.employees || [];
        setEmployees(list);
      })
      .catch(() => setEmployees([]));

    axios
      .get(`${API_URL}/api/getServices`)
      .then((res) => setServices(Array.isArray(res.data) ? res.data : []))
      .catch(() => setServices([]));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this department?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/deleteDepartment/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert("Department deleted successfully");
      fetchDepartments();
    } catch {
      alert("Error deleting department");
    }
  };

  const handleUpdate = (id) => navigate(`/admin/updateDepartment/${id}`);

  const chartData = useMemo(() => {
    const deptNames = departments.map((d) => d.deptName);

    const employeeMap = employees.reduce((acc, emp) => {
      const name = emp?.department?.deptName || emp?.department || "Unknown";
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});

    const serviceMap = services.reduce((acc, svc) => {
      const name = svc?.deptId?.deptName || "Unknown";
      acc[name] = (acc[name] || 0) + 1;
      return acc;
    }, {});

    return {
      labels: deptNames,
      employeeCounts: deptNames.map((name) => employeeMap[name] || 0),
      serviceCounts: deptNames.map((name) => serviceMap[name] || 0),
    };
  }, [departments, employees, services]);

  const axisStyle = {
    x: {
      ticks: {
        color: "#111827",
        padding: 8,
        font: { size: 13, weight: "600" },
      },
      grid: { color: "rgba(148, 163, 184, 0.28)" },
    },
    y: {
      ticks: {
        color: "#111827",
        padding: 6,
      },
      grid: { color: "rgba(148, 163, 184, 0.28)" },
    },
  };

  return (
    <div className="dept-page">
      <div className="dept-shell">
        <div className="dept-hero">
          <div>
            <p className="dept-eyebrow">Organization Setup</p>
            <h2>Department List ({departments.length})</h2>
            <span>Manage departments and monitor employee-service distribution</span>
          </div>

          <Link to="/admin/addDepartment" className="dept-add-btn">
            Add Department
          </Link>
        </div>

        <div className="dept-table-card">
          <div className="table-responsive">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Dept. ID</th>
                  <th>Department Name</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {departments.length > 0 ? (
                  departments.map((dept) => (
                    <tr key={dept._id}>
                      <td>
                        <span
                          role="button"
                          tabIndex={0}
                          style={{ cursor: "pointer" }}
                          onClick={() => handleUpdate(dept._id)}
                          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleUpdate(dept._id)}
                        >
                          {dept.deptId}
                        </span>
                      </td>
                      <td>{dept.deptName}</td>
                      <td>
                        <div className="d-flex gap-2 flex-wrap">
                          <button className="btn dept-action-btn edit" onClick={() => handleUpdate(dept._id)}>
                            Edit
                          </button>
                          <button className="btn dept-action-btn delete" onClick={() => handleDelete(dept._id)}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="text-center text-muted py-4">
                      No Departments Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="dept-chart-card">
          <h5>Employees Per Department</h5>
          <div className="dept-chart-canvas">
            <Bar
              data={{
                labels: chartData.labels,
                datasets: [
                  {
                    data: chartData.employeeCounts,
                    backgroundColor: "#003e6d",
                    borderRadius: 12,
                  },
                ],
              }}
              options={{
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: axisStyle,
              }}
            />
          </div>
        </div>

        <div className="dept-chart-card secondary">
          <h6>Services Per Department</h6>
          <div className="dept-chart-canvas">
            <Line
              data={{
                labels: chartData.labels,
                datasets: [
                  {
                    data: chartData.serviceCounts,
                    borderColor: "#003e6d",
                    backgroundColor: "rgba(0,62,109,0.18)",
                    tension: 0.4,
                    fill: true,
                  },
                ],
              }}
              options={{
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: axisStyle,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Department;
